import { AppData } from '@lightningjs/sdk';
import { getConfig, getShowByShowSlug, getVideos } from 'services/cwService';
import TivoApiClient from 'services/TivoApiClient';
import {
  TivoRecommendedResponse,
  TivoScreenResponse,
  TivoSearchResponse,
  TivoTrendingResponse,
  parseMediaIdentifersFromSearchResponse,
  parseMediaIdentifiersFromTrending,
  parseDetailsRecommended,
  parseEndCardRecommended,
  parseTivoScreens,
} from 'services/tivoData';
import { Platform } from 'models/platforms/platform';
import { AxiosResponse, Method } from 'axios';
import { HttpOptions } from 'services/CwApiClient';
import ApiServiceError from 'support/ApiServiceError';
import { ErrorType } from 'types/analytics';
import {
  Channel,
  Episode,
  MediaIdentifier,
  Show,
  Video,
} from 'types/api/media';
import { Lane } from 'services/cwData';

type TivoParamKey = 'guid' | 'device' | 'cwuid' | 'searchterm';
type TivoParams = Partial<Record<TivoParamKey, string>>;

const client = new TivoApiClient();

const sendRequest = async <TResult, TResponse>(
  method: Method,
  endpoint: string,
  parser?: (response: TResponse) => TResult,
  options?: HttpOptions,
) => {
  const response = await client.request<TResponse>(method, endpoint, options);
  return handleResponse<TResult, TResponse>(response, parser);
};

const handleResponse = <TResult, TResponse>(
  response: AxiosResponse,
  parser?: (response: TResponse) => TResult,
): TResult => {
  let result: TResult;
  if (parser) {
    try {
      result = parser(response.data);
    } catch (e) {
      throw new ApiServiceError(ErrorType.MISSING_CONTENT);
    }
  } else {
    result = response.data as TResult;
  }

  return result;
};

const createFilledEndpoint = (endpoint: string, params: TivoParams = {}) => {
  let completedEndpoint = endpoint;
  const staticTivoParams: TivoParams = {
    device: getDeviceParam(),
    cwuid: AppData!.mParticle.mpid || 'null', // Tivo rejects empty id
  };
  Object.entries({ ...staticTivoParams, ...params }).forEach(([key, value]) => {
    completedEndpoint = completedEndpoint.replace(`{${key}}`, value);
  });

  return completedEndpoint;
};

const getDeviceParam = () => {
  switch (AppData!.device.getPlatform()) {
    case Platform.TIZEN:
      return 'tizen';
    case Platform.LG:
      return 'lg';
    case Platform.VIZIO:
      return 'vizio';
    case Platform.COMCAST:
      return 'comcast';
    default:
      return 'tizen';
  }
};

export const getSearch = async (searchTerm: string) => {
  if (!AppData?.tivoEndpoints?.search) await getConfig();

  const endpoint = createFilledEndpoint(AppData!.tivoEndpoints!.search.url, {
    searchterm: searchTerm,
  });

  return await sendRequest<(Channel | Episode | Show)[], TivoSearchResponse>(
    'GET',
    endpoint,
    parseMediaIdentifersFromSearchResponse,
  );
};

export const getDetailsRecommended = async (guid: string) => {
  if (!AppData?.tivoEndpoints?.details_recommended_v2) await getConfig();

  const endpoint = createFilledEndpoint(
    AppData!.tivoEndpoints!.details_recommended_v2.url,
    {
      guid,
    },
  );

  return await sendRequest<Show[], TivoRecommendedResponse>(
    'GET',
    endpoint,
    parseDetailsRecommended,
  );
};

export const getTrending = async () => {
  if (!AppData?.tivoEndpoints?.trending) await getConfig();

  const endpoint = createFilledEndpoint(AppData!.tivoEndpoints!.trending.url);

  return await sendRequest<MediaIdentifier[], TivoTrendingResponse>(
    'GET',
    endpoint,
    parseMediaIdentifiersFromTrending,
  );
};

export const getContinueEmpty = async () => {
  if (!AppData?.tivoEndpoints?.continue_empty) await getConfig();

  const endpoint = createFilledEndpoint(
    AppData!.tivoEndpoints!.continue_empty.url,
  );

  return await sendRequest<MediaIdentifier[], TivoTrendingResponse>(
    'GET',
    endpoint,
    parseMediaIdentifiersFromTrending,
  );
};

export const getEndCardRecommended = async (guid: string) => {
  if (!AppData?.tivoEndpoints?.recommend) await getConfig();
  const endpoint = createFilledEndpoint(AppData!.tivoEndpoints!.recommend.url, {
    guid,
  });

  return await sendRequest<(Episode | Show)[], TivoRecommendedResponse>(
    'GET',
    endpoint,
    parseEndCardRecommended,
  );
};

export const getTivoApiScreens = async (apiUrl: string | undefined) => {
  if (!apiUrl) return [];

  const endpoint = createFilledEndpoint(apiUrl);
  return await sendRequest<Lane[], TivoScreenResponse>(
    'GET',
    endpoint,
    parseTivoScreens,
  );
};

const batchRequestVideosFromIdentifiers = async (
  identifiers: MediaIdentifier[],
) => {
  if (!identifiers.length) return [];

  const guidList: string[] = identifiers.reduce<string[]>((acc, elem) => {
    if (elem.guid) acc.push(elem.guid);
    return acc;
  }, []);

  return await getVideos(guidList);
};

const batchRequestShowsFromIdentifiers = async (
  identifiers: MediaIdentifier[],
) => {
  if (!identifiers.length) return [];
  const requests: Promise<Show | null>[] = [];

  identifiers.forEach(identifier => {
    if (identifier.slug) requests.push(getShowByShowSlug(identifier.slug));
  });

  const response: Array<Show> = [];
  (await Promise.allSettled(requests)).forEach(result => {
    if (result.status === 'fulfilled' && result.value)
      response.push(result.value);
  });

  return response;
};

export const getMediaDataFromIdentifiers = async (
  identifiers: MediaIdentifier[],
) => {
  const videoIdentifiers = identifiers.filter(
    identifier => identifier.objectType === 'Episode',
  );
  const showIdentifiers = identifiers.filter(
    identifier => identifier.objectType !== 'Episode',
  );

  const [videoData, showData] = await Promise.all([
    batchRequestVideosFromIdentifiers(videoIdentifiers),
    batchRequestShowsFromIdentifiers(showIdentifiers),
  ]);

  return identifiers
    .map(identifier => {
      if (identifier.objectType === 'Episode') {
        return videoData.find(data => data.guid === identifier.guid);
      } else {
        return showData.find(data => data.showSlug === identifier.slug);
      }
    })
    .filter(value => value !== undefined) as (Show | Video)[];
};
